<template>
  <v-card outlined class="mt-4 rounded-lg">
    <v-card-title class="subtitle-2 font-weight-bold py-3 px-4 red darken-1 white--text">
      <v-icon left color="white" small>mdi-close-circle-outline</v-icon>
      Dado de Baja
      <v-spacer />
      <v-progress-circular v-if="loading" indeterminate color="white" size="14" width="2" />
    </v-card-title>

    <v-card-text class="pt-3 pb-3">

      <!-- Programa ya en BAJA -->
      <v-alert v-if="esBaja" type="error" dense outlined class="mb-0" icon="mdi-close-circle">
        Este programa ya está dado de baja.
      </v-alert>

      <!-- Hay solicitud SOLICITADO pendiente -->
      <div v-else-if="solicitudPendiente">
        <div class="d-flex align-center" style="gap:8px">
          <v-chip small color="yellow darken-3" dark label>
            <v-icon left small>mdi-clock-outline</v-icon>
            Pendiente de aprobación
          </v-chip>
          <span class="caption grey--text">{{ solicitudPendiente.motivo_baja_programa_nombre }}</span>
        </div>
        <div class="caption grey--text mt-1">
          Solicitado el {{ (solicitudPendiente.fecha_registro || '').slice(0, 10) }}
          por {{ solicitudPendiente.solicitante_nombres }} {{ solicitudPendiente.solicitante_primer_apellido }}
        </div>
        <div v-if="solicitudPendiente.observacion" class="caption mt-1">{{ solicitudPendiente.observacion }}</div>

        <!-- Botones aprobar/rechazar para aprobadores -->
        <div v-if="esAprobador" class="d-flex mt-2" style="gap:8px">
          <v-btn x-small color="green darken-1" dark :loading="procesando === 'ap'" @click="iniciarAprobar">
            <v-icon left x-small>mdi-check</v-icon> Aprobar
          </v-btn>
          <v-btn x-small color="red darken-1" outlined :disabled="procesando !== null" @click="dialogRechazar = true">
            <v-icon left x-small>mdi-close</v-icon> Rechazar
          </v-btn>
        </div>
      </div>

      <!-- Sin solicitud pendiente y no está en BAJA -->
      <div v-else>
        <v-btn small color="red darken-1" outlined @click="abrirDialog">
          <v-icon left small>mdi-close-circle-outline</v-icon>
          Solicitar dado de baja
        </v-btn>
      </div>

      <!-- Historial (solicitudes previas no pendientes) -->
      <div v-if="historialPrevio.length > 0" class="mt-3">
        <div class="caption grey--text mb-1 font-weight-medium">Historial de solicitudes</div>
        <div v-for="sol in historialPrevio" :key="sol.id" class="mb-1 d-flex align-start" style="gap:8px">
          <v-chip x-small dark :color="colorEstado(sol.estado)">{{ sol.estado }}</v-chip>
          <div>
            <div class="caption">{{ sol.motivo_baja_programa_nombre }}</div>
            <div class="caption grey--text">{{ (sol.fecha_registro || '').slice(0, 10) }}</div>
            <div v-if="sol.respuesta" class="caption red--text text--darken-2">↳ {{ sol.respuesta }}</div>
          </div>
        </div>
      </div>

    </v-card-text>

    <!-- Dialog: confirmar aprobar -->
    <v-dialog v-model="dialogAprobar" max-width="440" persistent>
      <v-card>
        <v-toolbar color="green darken-1" dark dense flat>
          <v-icon class="mr-2">mdi-check-circle-outline</v-icon>
          <v-toolbar-title>Aprobar dado de baja</v-toolbar-title>
          <v-spacer />
          <v-btn icon :disabled="procesando !== null" @click="dialogAprobar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-alert v-if="inscritosEfectivos > 0" type="warning" dense outlined>
            Al aprobar, <strong>{{ inscritosEfectivos }} inscripción(es) activa(s)</strong> pasarán automáticamente a RETIRADO.
          </v-alert>
          <p class="body-2">¿Confirmas que deseas aprobar la solicitud de dado de baja?</p>
          <div v-if="solicitudPendiente" class="caption grey--text">
            Causa: {{ solicitudPendiente.motivo_baja_programa_nombre }}
            <span v-if="solicitudPendiente.observacion"> · {{ solicitudPendiente.observacion }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="procesando !== null" @click="dialogAprobar = false">Cancelar</v-btn>
          <v-btn color="green darken-1" dark :loading="procesando === 'ap'" @click="ejecutarAprobar">
            <v-icon left small>mdi-check</v-icon> Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: rechazar -->
    <v-dialog v-model="dialogRechazar" max-width="440" persistent>
      <v-card>
        <v-toolbar color="red darken-1" dark dense flat>
          <v-icon class="mr-2">mdi-close-circle-outline</v-icon>
          <v-toolbar-title>Rechazar solicitud</v-toolbar-title>
          <v-spacer />
          <v-btn icon :disabled="procesando !== null" @click="dialogRechazar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-textarea
            v-model="formRechazar.respuesta"
            label="Motivo del rechazo *"
            outlined dense rows="3" auto-grow
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="procesando !== null" @click="dialogRechazar = false">Cancelar</v-btn>
          <v-btn color="red darken-1" dark :loading="procesando === 're'" :disabled="!formRechazar.respuesta" @click="ejecutarRechazar">
            <v-icon left small>mdi-close</v-icon> Rechazar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: solicitar baja -->
    <v-dialog v-model="dialogSolicitar" max-width="480" persistent>
      <v-card>
        <v-toolbar color="red darken-1" dark dense flat>
          <v-icon class="mr-2">mdi-close-circle-outline</v-icon>
          <v-toolbar-title>Solicitar dado de baja</v-toolbar-title>
          <v-spacer />
          <v-btn icon :disabled="guardando" @click="dialogSolicitar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-select
            v-model="form.motivo_baja_programa_id"
            :items="motivosFiltrados"
            item-text="nombre"
            item-value="id"
            label="Causa de la baja *"
            outlined dense
            :loading="loadingMotivos"
            no-data-text="Cargando causas..."
          />
          <v-textarea
            v-model="form.observacion"
            label="Observación (opcional)"
            outlined dense rows="3" auto-grow class="mt-1"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="guardando" @click="dialogSolicitar = false">Cancelar</v-btn>
          <v-btn
            color="red darken-1" dark
            :loading="guardando"
            :disabled="!form.motivo_baja_programa_id"
            @click="solicitar"
          >
            <v-icon left small>mdi-send</v-icon> Solicitar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="4000">
      {{ snackbar.msg }}
      <template v-slot:action>
        <v-btn text small @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import xaxios from '@/xaxios';

const API_URL = `${process.env.VUE_APP_API_REST}/api/v1`;

export default {
  name: 'SolicitudDadoDeBaja',

  props: {
    programaId: { type: [Number, String], required: true },
    estadoProgramaCodigo: { type: String, default: null },
    inscritosEfectivos: { type: Number, default: 0 },
  },

  data() {
    return {
      historial: [],
      loading: false,
      motivos: [],
      loadingMotivos: false,
      dialogSolicitar: false,
      dialogAprobar: false,
      dialogRechazar: false,
      guardando: false,
      procesando: null,
      form: { motivo_baja_programa_id: null, observacion: '' },
      formRechazar: { respuesta: '' },
      snackbar: { show: false, msg: '', color: 'success' },
    };
  },

  computed: {
    esBaja() {
      return this.estadoProgramaCodigo === 'BAJA';
    },
    solicitudPendiente() {
      return this.historial.find((s) => s.estado === 'SOLICITADO') || null;
    },
    historialPrevio() {
      return this.historial.filter((s) => s.estado !== 'SOLICITADO');
    },
    motivosFiltrados() {
      return this.motivos.filter((m) => m.activo);
    },
    esAprobador() {
      const cargo = this.$store.state.CARGO;
      return cargo && [1, 26].includes(cargo.id);
    },
  },

  mounted() {
    this.cargarHistorial();
  },

  methods: {
    async cargarHistorial() {
      this.loading = true;
      try {
        const { data } = await xaxios.get(`${API_URL}/programa/${this.programaId}/dado-de-baja/solicitudes`);
        this.historial = data?.data?.historial || [];
      } catch (e) {
        window.console.error('Error cargando historial baja:', e);
      } finally {
        this.loading = false;
      }
    },

    async cargarMotivos() {
      if (this.motivos.length > 0) return;
      this.loadingMotivos = true;
      try {
        const { data } = await xaxios.get(`${API_URL}/dado-de-baja/motivos`);
        this.motivos = data?.data?.data || data?.data || [];
      } catch (e) {
        window.console.error('Error cargando motivos:', e);
      } finally {
        this.loadingMotivos = false;
      }
    },

    abrirDialog() {
      this.form = { motivo_baja_programa_id: null, observacion: '' };
      this.dialogSolicitar = true;
      this.cargarMotivos();
    },

    async solicitar() {
      if (!this.form.motivo_baja_programa_id) return;
      this.guardando = true;
      try {
        const body = { motivo_baja_programa_id: this.form.motivo_baja_programa_id };
        if (this.form.observacion) body.observacion = this.form.observacion;
        await xaxios.post(`${API_URL}/programa/${this.programaId}/dado-de-baja/solicitudes`, body);
        this.dialogSolicitar = false;
        this.notificar('Solicitud de dado de baja enviada. Quedará pendiente de aprobación.', 'success');
        await this.cargarHistorial();
      } catch (err) {
        const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al enviar la solicitud';
        this.notificar(msg, 'error');
      } finally {
        this.guardando = false;
      }
    },

    iniciarAprobar() {
      this.dialogAprobar = true;
    },

    async ejecutarAprobar() {
      if (!this.solicitudPendiente) return;
      this.procesando = 'ap';
      try {
        await xaxios.patch(`${API_URL}/dado-de-baja/solicitudes/${this.solicitudPendiente.id}`, { estado: 'APROBADO' });
        this.dialogAprobar = false;
        this.notificar('Solicitud aprobada. El programa fue dado de baja.', 'success');
        await this.cargarHistorial();
        this.$emit('baja-aprobada');
      } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
          this.notificar('No tienes permiso para aprobar en esta sede.', 'error');
        } else {
          const msg = err?.response?.data?.error || 'Error al aprobar la solicitud';
          this.notificar(msg, 'error');
        }
      } finally {
        this.procesando = null;
      }
    },

    async ejecutarRechazar() {
      if (!this.solicitudPendiente || !this.formRechazar.respuesta) return;
      this.procesando = 're';
      try {
        await xaxios.patch(`${API_URL}/dado-de-baja/solicitudes/${this.solicitudPendiente.id}`, {
          estado: 'RECHAZADO',
          respuesta: this.formRechazar.respuesta,
        });
        this.dialogRechazar = false;
        this.formRechazar.respuesta = '';
        this.notificar('Solicitud rechazada.', 'info');
        await this.cargarHistorial();
      } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
          this.notificar('No tienes permiso para rechazar en esta sede.', 'error');
        } else {
          const msg = err?.response?.data?.error || 'Error al rechazar la solicitud';
          this.notificar(msg, 'error');
        }
      } finally {
        this.procesando = null;
      }
    },

    colorEstado(estado) {
      if (estado === 'APROBADO') return 'red darken-1';
      if (estado === 'RECHAZADO') return 'grey darken-1';
      return 'yellow darken-3';
    },

    notificar(msg, color = 'success') {
      this.snackbar = { show: true, msg, color };
    },
  },
};
</script>
